// src/pages/superadmin/SuperAdminPartners.jsx
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
  Users2, Plus, Search, Filter, Edit2, Trash2, X, Check,
  ExternalLink, Tag, MapPin, Phone, Mail, Globe, Star,
  ChevronDown, AlertCircle, RefreshCw, Building2, Car,
  Hotel, Utensils, Wrench, Plane, Bus, Zap, BookOpen,
  ShieldCheck, Heart, Package, ToggleLeft, ToggleRight
} from 'lucide-react';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:8001';

// ─── Catégories ───────────────────────────────────────────────────────────────
const CATEGORIES = [
  { value: 'car_rental',  label: 'Car Rental',   icon: Car,        color: '#38bdf8' },
  { value: 'hotel',       label: 'Hotel',         icon: Hotel,      color: '#a78bfa' },
  { value: 'catering',    label: 'Catering',      icon: Utensils,   color: '#fb923c' },
  { value: 'fuel',        label: 'Fuel',          icon: Zap,        color: '#facc15' },
  { value: 'maintenance', label: 'Maintenance',   icon: Wrench,     color: '#f87171' },
  { value: 'shuttle',     label: 'Shuttle',       icon: Bus,        color: '#34d399' },
  { value: 'charter',     label: 'Charter',       icon: Plane,      color: '#60a5fa' },
  { value: 'avionics',    label: 'Avionics',      icon: Package,    color: '#c084fc' },
  { value: 'training',    label: 'Training',      icon: BookOpen,   color: '#f472b6' },
  { value: 'customs',     label: 'Customs',       icon: ShieldCheck,color: '#94a3b8' },
  { value: 'medical',     label: 'Medical',       icon: Heart,      color: '#fb7185' },
  { value: 'insurance',   label: 'Insurance',     icon: ShieldCheck,color: '#818cf8' },
  { value: 'handling',    label: 'Handling',      icon: Building2,  color: '#2dd4bf' },
  { value: 'other',       label: 'Other',         icon: Package,    color: '#64748b' },
];

const getCategoryMeta = (value) =>
  CATEGORIES.find(c => c.value === value) || CATEGORIES[CATEGORIES.length - 1];

// ─── Formulaire vide ──────────────────────────────────────────────────────────
const EMPTY_FORM = {
  name: '', category: 'car_rental', airport_icao: '', city: '', state: '',
  country: 'USA', address: '', zip_code: '', latitude: '', longitude: '',
  phone: '', email: '', website: '',
  booking_url: '', affiliate_url: '', partner_code: '',
  description: '', price_range: '', languages: '', rating: '', priority: 0,
  is_active: true,
};

// ─── Badge catégorie ──────────────────────────────────────────────────────────
const CategoryBadge = ({ category }) => {
  const meta = getCategoryMeta(category);
  const Icon = meta.icon;
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium"
      style={{ background: `${meta.color}20`, color: meta.color, border: `1px solid ${meta.color}40` }}>
      <Icon size={10} />
      {meta.label}
    </span>
  );
};

// ─── Stars ────────────────────────────────────────────────────────────────────
const StarRating = ({ value }) => {
  if (!value) return <span className="text-gray-600 text-xs">—</span>;
  return (
    <span className="flex items-center gap-0.5">
      <Star size={11} className="text-yellow-400 fill-yellow-400" />
      <span className="text-yellow-400 text-xs font-mono">{Number(value).toFixed(1)}</span>
    </span>
  );
};

// ─── Field — défini EN DEHORS du modal pour éviter le remount à chaque keystroke ──
const Field = ({ label, name, type = 'text', placeholder = '', half = false, form, set }) => (
  <div className={half ? 'col-span-1' : 'col-span-2'}>
    <label className="block text-xs text-gray-400 mb-1">{label}</label>
    <input
      type={type}
      value={form[name] ?? ''}
      onChange={e => set(name, e.target.value)}
      placeholder={placeholder}
      className="w-full bg-gray-700/60 border border-gray-600/60 rounded-lg px-3 py-2 text-sm text-white placeholder-gray-500 focus:outline-none focus:border-blue-500/70 transition-colors"
    />
  </div>
);

// ─── Modal Formulaire ─────────────────────────────────────────────────────────
const PartnerModal = ({ partner, onClose, onSave }) => {
  const [form, setForm] = useState(partner ? { ...partner } : { ...EMPTY_FORM });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const set = (key, val) => setForm(f => ({ ...f, [key]: val }));

  const handleSubmit = async () => {
    if (!form.name.trim()) { setError('Name is required.'); return; }
    if (!form.category)    { setError('Category is required.'); return; }
    setSaving(true); setError('');
    try {
      await onSave(form);
      onClose();
    } catch (e) {
      setError(e.response?.data?.detail || 'Save failed.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4">
      <div className="bg-gray-900 border border-gray-700/80 rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-700/60 sticky top-0 bg-gray-900 z-10">
          <div className="flex items-center gap-2">
            <Users2 size={18} className="text-cyan-400" />
            <h2 className="text-white font-semibold text-sm">
              {partner ? 'Edit Partner' : 'New Partner'}
            </h2>
          </div>
          <button onClick={onClose} className="text-gray-500 hover:text-white transition-colors">
            <X size={18} />
          </button>
        </div>

        {/* Body */}
        <div className="p-5 space-y-5">
          {error && (
            <div className="flex items-center gap-2 bg-red-900/30 border border-red-700/50 rounded-lg px-3 py-2 text-red-400 text-xs">
              <AlertCircle size={14} /> {error}
            </div>
          )}

          {/* Section : Identity */}
          <div>
            <p className="text-gray-500 text-xs uppercase tracking-wider mb-3">Identity</p>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Partner Name *" name="name" placeholder="DFW Executive Car Rental" form={form} set={set} />
              <div className="col-span-2">
                <label className="block text-xs text-gray-400 mb-1">Category *</label>
                <div className="grid grid-cols-4 gap-1.5">
                  {CATEGORIES.map(c => {
                    const Icon = c.icon;
                    const active = form.category === c.value;
                    return (
                      <button key={c.value} onClick={() => set('category', c.value)}
                        className="flex items-center gap-1.5 px-2 py-1.5 rounded-lg text-xs font-medium transition-all border"
                        style={active
                          ? { background: `${c.color}20`, color: c.color, borderColor: `${c.color}60` }
                          : { background: 'transparent', color: '#6b7280', borderColor: '#374151' }
                        }>
                        <Icon size={11} /> {c.label}
                      </button>
                    );
                  })}
                </div>
              </div>
              <div className="col-span-2">
                <label className="block text-xs text-gray-400 mb-1">Description</label>
                <textarea value={form.description} onChange={e => set('description', e.target.value)}
                  rows={2} placeholder="Short description shown to pilots..."
                  className="w-full bg-gray-700/60 border border-gray-600/60 rounded-lg px-3 py-2 text-sm text-white placeholder-gray-500 focus:outline-none focus:border-blue-500/70 resize-none transition-colors" />
              </div>
            </div>
          </div>

          {/* Section : Location */}
          <div>
            <p className="text-gray-500 text-xs uppercase tracking-wider mb-3">Location <span className="text-gray-600 normal-case">(leave ICAO blank = global partner)</span></p>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Airport ICAO" name="airport_icao" placeholder="KDFW" half form={form} set={set} />
              <Field label="City" name="city" placeholder="Dallas" half form={form} set={set} />
              <Field label="State" name="state" placeholder="TX" half form={form} set={set} />
              <Field label="Country" name="country" placeholder="USA" half form={form} set={set} />
              <Field label="Address" name="address" placeholder="123 Aviation Blvd" form={form} set={set} />
              <Field label="ZIP Code" name="zip_code" placeholder="75001" half form={form} set={set} />
              <Field label="Latitude" name="latitude" placeholder="32.8998" half form={form} set={set} />
              <Field label="Longitude" name="longitude" placeholder="-97.0403" half form={form} set={set} />
            </div>
          </div>

          {/* Section : Contact */}
          <div>
            <p className="text-gray-500 text-xs uppercase tracking-wider mb-3">Contact</p>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Phone" name="phone" placeholder="+1-214-555-0101" half form={form} set={set} />
              <Field label="Email" name="email" placeholder="info@partner.com" half form={form} set={set} />
              <Field label="Website" name="website" placeholder="https://partner.com" form={form} set={set} />
            </div>
          </div>

          {/* Section : Booking */}
          <div>
            <p className="text-gray-500 text-xs uppercase tracking-wider mb-3">Booking & Codes</p>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Booking URL" name="booking_url" placeholder="https://partner.com/book" form={form} set={set} />
              <Field label="Affiliate URL" name="affiliate_url" placeholder="https://partner.com/aff?ref=aero" form={form} set={set} />
              <Field label="Partner Code" name="partner_code" placeholder="AERO2024" half form={form} set={set} />
              <Field label="Price Range" name="price_range" placeholder="$50–$120/day" half form={form} set={set} />
            </div>
          </div>

          {/* Section : Meta */}
          <div>
            <p className="text-gray-500 text-xs uppercase tracking-wider mb-3">Metadata</p>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Languages" name="languages" placeholder="EN, FR, ES" half form={form} set={set} />
              <Field label="Rating (0–5)" name="rating" type="number" placeholder="4.8" half form={form} set={set} />
              <Field label="Priority (higher = first)" name="priority" type="number" placeholder="10" half form={form} set={set} />
              <div className="col-span-1 flex items-center gap-3 pt-5">
                <button onClick={() => set('is_active', !form.is_active)}
                  className="flex items-center gap-2 text-sm">
                  {form.is_active
                    ? <ToggleRight size={22} className="text-emerald-400" />
                    : <ToggleLeft size={22} className="text-gray-500" />
                  }
                  <span className={form.is_active ? 'text-emerald-400' : 'text-gray-500'}>
                    {form.is_active ? 'Active' : 'Inactive'}
                  </span>
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-3 px-5 py-4 border-t border-gray-700/60 sticky bottom-0 bg-gray-900">
          <button onClick={onClose}
            className="px-4 py-2 text-sm text-gray-400 hover:text-white border border-gray-700 rounded-lg transition-colors">
            Cancel
          </button>
          <button onClick={handleSubmit} disabled={saving}
            className="flex items-center gap-2 px-4 py-2 text-sm font-medium bg-cyan-500/20 text-cyan-400 border border-cyan-500/40 rounded-lg hover:bg-cyan-500/30 transition-colors disabled:opacity-50">
            {saving ? <RefreshCw size={14} className="animate-spin" /> : <Check size={14} />}
            {partner ? 'Save Changes' : 'Create Partner'}
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── Composant principal ──────────────────────────────────────────────────────
const SuperAdminPartners = () => {
  const [partners, setPartners]       = useState([]);
  const [loading, setLoading]         = useState(true);
  const [search, setSearch]           = useState('');
  const [filterCat, setFilterCat]     = useState('');
  const [filterActive, setFilterActive] = useState('');
  const [modal, setModal]             = useState(null); // null | 'create' | partner object
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting]       = useState(false);
  const [toast, setToast]             = useState('');

  const showToast = (msg) => {
    setToast(msg);
    setTimeout(() => setToast(''), 3000);
  };

  const fetchPartners = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_BASE}/admin/partners`);
      setPartners(res.data || []);
    } catch (e) {
      console.error('Error fetching partners:', e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchPartners(); }, [fetchPartners]);

  const handleSave = async (form) => {
    const payload = {
      ...form,
      rating:    form.rating   !== '' ? parseFloat(form.rating)   : null,
      priority:  form.priority !== '' ? parseInt(form.priority)   : 0,
      latitude:  form.latitude  !== '' ? parseFloat(form.latitude)  : null,
      longitude: form.longitude !== '' ? parseFloat(form.longitude) : null,
      airport_icao: form.airport_icao?.trim().toUpperCase() || null,
      city:      form.city?.trim()  || null,
      state:     form.state?.trim() || null,
    };

    if (modal && modal.id) {
      await axios.put(`${API_BASE}/admin/partners/${modal.id}`, payload);
      showToast('Partner updated.');
    } else {
      await axios.post(`${API_BASE}/admin/partners`, payload);
      showToast('Partner created.');
    }
    fetchPartners();
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await axios.delete(`${API_BASE}/admin/partners/${deleteTarget.id}`);
      showToast('Partner deleted.');
      setDeleteTarget(null);
      fetchPartners();
    } catch (e) {
      console.error('Delete failed:', e);
    } finally {
      setDeleting(false);
    }
  };

  const toggleActive = async (partner) => {
    try {
      await axios.patch(`${API_BASE}/admin/partners/${partner.id}`, {
        is_active: !partner.is_active,
      });
      fetchPartners();
    } catch (e) {
      console.error('Toggle failed:', e);
    }
  };

  // Filtres
  const filtered = partners.filter(p => {
    const matchSearch = !search || [p.name, p.city, p.airport_icao, p.description]
      .some(v => v?.toLowerCase().includes(search.toLowerCase()));
    const matchCat    = !filterCat    || p.category === filterCat;
    const matchActive = filterActive === '' || String(p.is_active) === filterActive;
    return matchSearch && matchCat && matchActive;
  });

  // Stats rapides
  const stats = {
    total:    partners.length,
    active:   partners.filter(p => p.is_active).length,
    global:   partners.filter(p => !p.airport_icao && !p.city).length,
    cats:     [...new Set(partners.map(p => p.category))].length,
  };

  return (
    <div className="space-y-5">
      {/* Toast */}
      {toast && (
        <div className="fixed top-4 right-4 z-50 bg-emerald-900/90 border border-emerald-600/60 text-emerald-300 text-sm px-4 py-2.5 rounded-xl shadow-xl flex items-center gap-2 animate-fade-in">
          <Check size={14} /> {toast}
        </div>
      )}

      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Users2 size={18} className="text-cyan-400" />
          <h2 className="text-white font-semibold">Partners</h2>
          <span className="text-gray-500 text-xs">{stats.total} total</span>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={fetchPartners}
            className="p-2 bg-gray-800 border border-gray-700 rounded-lg text-gray-400 hover:text-white transition-colors">
            <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
          </button>
          <button onClick={() => setModal('create')}
            className="flex items-center gap-1.5 px-3 py-2 bg-cyan-500/20 text-cyan-400 border border-cyan-500/40 rounded-lg text-sm font-medium hover:bg-cyan-500/30 transition-colors">
            <Plus size={14} /> Add Partner
          </button>
        </div>
      </div>

      {/* KPIs rapides */}
      <div className="grid grid-cols-4 gap-3">
        {[
          { label: 'Total',      value: stats.total,  color: '#38bdf8' },
          { label: 'Active',     value: stats.active, color: '#34d399' },
          { label: 'Global',     value: stats.global, color: '#a78bfa' },
          { label: 'Categories', value: stats.cats,   color: '#fb923c' },
        ].map(s => (
          <div key={s.label} className="bg-gray-800/60 border border-gray-700/60 rounded-xl p-3 text-center">
            <p className="text-2xl font-bold tabular-nums" style={{ color: s.color }}>{s.value}</p>
            <p className="text-gray-500 text-xs mt-0.5">{s.label}</p>
          </div>
        ))}
      </div>

      {/* Filtres */}
      <div className="flex flex-wrap items-center gap-2">
        {/* Search */}
        <div className="relative flex-1 min-w-48">
          <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
          <input value={search} onChange={e => setSearch(e.target.value)}
            placeholder="Search name, city, ICAO..."
            className="w-full bg-gray-800/60 border border-gray-700/60 rounded-lg pl-8 pr-3 py-2 text-sm text-white placeholder-gray-500 focus:outline-none focus:border-gray-500 transition-colors" />
        </div>
        {/* Category filter */}
        <div className="relative">
          <Filter size={12} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-500" />
          <select value={filterCat} onChange={e => setFilterCat(e.target.value)}
            className="bg-gray-800/60 border border-gray-700/60 rounded-lg pl-7 pr-6 py-2 text-sm text-gray-300 focus:outline-none appearance-none cursor-pointer">
            <option value="">All categories</option>
            {CATEGORIES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
          </select>
          <ChevronDown size={11} className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 pointer-events-none" />
        </div>
        {/* Active filter */}
        <div className="relative">
          <select value={filterActive} onChange={e => setFilterActive(e.target.value)}
            className="bg-gray-800/60 border border-gray-700/60 rounded-lg px-3 py-2 text-sm text-gray-300 focus:outline-none appearance-none cursor-pointer pr-6">
            <option value="">All status</option>
            <option value="true">Active</option>
            <option value="false">Inactive</option>
          </select>
          <ChevronDown size={11} className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 pointer-events-none" />
        </div>
      </div>

      {/* Table */}
      {loading ? (
        <div className="flex justify-center py-16">
          <div className="animate-spin rounded-full h-7 w-7 border-b-2 border-cyan-500" />
        </div>
      ) : filtered.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16 text-gray-500">
          <Users2 size={32} className="mb-3 opacity-30" />
          <p className="text-sm">No partners found.</p>
          <button onClick={() => setModal('create')} className="mt-3 text-cyan-400 text-xs hover:underline">
            Add your first partner
          </button>
        </div>
      ) : (
        <div className="bg-gray-800/40 border border-gray-700/60 rounded-xl overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-700/60">
                {['Partner', 'Category', 'Location', 'Contact', 'Booking', 'Rating', 'Status', ''].map(h => (
                  <th key={h} className="text-left text-gray-500 text-xs font-medium uppercase tracking-wider px-4 py-3">
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-700/40">
              {filtered.map(p => (
                <tr key={p.id} className="group hover:bg-gray-700/20 transition-colors">
                  {/* Nom + description */}
                  <td className="px-4 py-3">
                    <div className="font-medium text-white text-sm">{p.name}</div>
                    {p.description && (
                      <div className="text-gray-500 text-xs mt-0.5 max-w-48 truncate">{p.description}</div>
                    )}
                    {p.partner_code && (
                      <div className="flex items-center gap-1 mt-1">
                        <Tag size={9} className="text-yellow-500" />
                        <span className="text-yellow-500 text-xs font-mono">{p.partner_code}</span>
                      </div>
                    )}
                  </td>

                  {/* Catégorie */}
                  <td className="px-4 py-3">
                    <CategoryBadge category={p.category} />
                  </td>

                  {/* Localisation */}
                  <td className="px-4 py-3">
                    {p.airport_icao ? (
                      <div className="flex items-center gap-1 text-blue-400 text-xs font-mono">
                        <Plane size={10} /> {p.airport_icao}
                      </div>
                    ) : p.city ? (
                      <div className="flex items-center gap-1 text-gray-400 text-xs">
                        <MapPin size={10} /> {p.city}{p.state ? `, ${p.state}` : ''}
                      </div>
                    ) : (
                      <span className="text-purple-400 text-xs">🌐 Global</span>
                    )}
                    {p.price_range && (
                      <div className="text-gray-500 text-xs mt-0.5">{p.price_range}</div>
                    )}
                  </td>

                  {/* Contact */}
                  <td className="px-4 py-3 space-y-1">
                    {p.phone && (
                      <div className="flex items-center gap-1 text-gray-400 text-xs">
                        <Phone size={9} /> {p.phone}
                      </div>
                    )}
                    {p.email && (
                      <div className="flex items-center gap-1 text-gray-400 text-xs">
                        <Mail size={9} /> <span className="max-w-32 truncate">{p.email}</span>
                      </div>
                    )}
                  </td>

                  {/* Booking */}
                  <td className="px-4 py-3 space-y-1">
                    {(p.booking_url || p.affiliate_url) && (
                      <a href={p.affiliate_url || p.booking_url} target="_blank" rel="noreferrer"
                        className="flex items-center gap-1 text-cyan-400 text-xs hover:underline">
                        <ExternalLink size={9} /> Booking link
                      </a>
                    )}
                    {p.website && (
                      <a href={p.website} target="_blank" rel="noreferrer"
                        className="flex items-center gap-1 text-gray-400 text-xs hover:underline">
                        <Globe size={9} /> Website
                      </a>
                    )}
                  </td>

                  {/* Rating */}
                  <td className="px-4 py-3">
                    <StarRating value={p.rating} />
                    {p.priority > 0 && (
                      <div className="text-gray-600 text-xs mt-0.5">P: {p.priority}</div>
                    )}
                  </td>

                  {/* Status toggle */}
                  <td className="px-4 py-3">
                    <button onClick={() => toggleActive(p)} title="Toggle active">
                      {p.is_active
                        ? <ToggleRight size={20} className="text-emerald-400 hover:text-emerald-300 transition-colors" />
                        : <ToggleLeft  size={20} className="text-gray-600 hover:text-gray-400 transition-colors" />
                      }
                    </button>
                  </td>

                  {/* Actions */}
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                      <button onClick={() => setModal(p)}
                        className="p-1.5 rounded-lg bg-blue-500/10 text-blue-400 hover:bg-blue-500/20 transition-colors">
                        <Edit2 size={12} />
                      </button>
                      <button onClick={() => setDeleteTarget(p)}
                        className="p-1.5 rounded-lg bg-red-500/10 text-red-400 hover:bg-red-500/20 transition-colors">
                        <Trash2 size={12} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Modal create/edit */}
      {modal && (
        <PartnerModal
          partner={modal === 'create' ? null : modal}
          onClose={() => setModal(null)}
          onSave={handleSave}
        />
      )}

      {/* Confirm delete */}
      {deleteTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm">
          <div className="bg-gray-900 border border-gray-700/80 rounded-2xl p-6 max-w-sm w-full mx-4 shadow-2xl">
            <div className="flex items-center gap-2 mb-3">
              <AlertCircle size={18} className="text-red-400" />
              <h3 className="text-white font-semibold text-sm">Delete Partner</h3>
            </div>
            <p className="text-gray-400 text-sm mb-5">
              Are you sure you want to delete <span className="text-white font-medium">{deleteTarget.name}</span>?
              This cannot be undone.
            </p>
            <div className="flex justify-end gap-3">
              <button onClick={() => setDeleteTarget(null)}
                className="px-4 py-2 text-sm text-gray-400 border border-gray-700 rounded-lg hover:text-white transition-colors">
                Cancel
              </button>
              <button onClick={handleDelete} disabled={deleting}
                className="flex items-center gap-2 px-4 py-2 text-sm font-medium bg-red-500/20 text-red-400 border border-red-500/40 rounded-lg hover:bg-red-500/30 transition-colors disabled:opacity-50">
                {deleting ? <RefreshCw size={13} className="animate-spin" /> : <Trash2 size={13} />}
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      <style>{`
        @keyframes fade-in {
          from { opacity: 0; transform: translateY(6px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        .animate-fade-in { animation: fade-in 0.2s ease-out; }
      `}</style>
    </div>
  );
};

export default SuperAdminPartners;